import { BufferGeometry } from 'three';
import { DECOR_VARIANTS, HAZARDS } from '@cloudpearl/config';
import type { DecorKind, HazardKind, MapDef } from '@cloudpearl/config';
import { Rng } from '@cloudpearl/core';
import { mixRGB, rgb, smoothRock, thinCards } from './geometry/builder';
import { bush, cactus, deadTree, dryBush, grassTuft, pineTree, roundTree } from './geometry/plants';
import { arch, gate, house, mesa, pillar, ruin, spire, waterfallRibbon } from './geometry/structures';

/** Material slots a prefab part can use. The host maps these to real materials. */
export type MaterialSlot =
  | 'stone'
  | 'mossStone'
  | 'sandstone'
  | 'cliff'
  | 'bark'
  | 'mossWood'
  | 'plaster'
  | 'roof'
  | 'cactus'
  | 'grassTop'
  | 'tuft'
  | 'foliage'
  | 'waterfall';

export interface PrefabPart {
  geometry: BufferGeometry;
  /** Cheaper stand-in for distant instances (thinned leaf cards). */
  far?: BufferGeometry;
  slot: MaterialSlot;
  castShadow: boolean;
}

export interface Prefab {
  parts: PrefabPart[];
}

/** Every visual kind (hazards/landmarks + decor). */
export type PrefabKind = HazardKind | DecorKind;

export type PrefabLibrary = Map<PrefabKind, Prefab[]>;

const plant = (p: { wood?: BufferGeometry; leaves?: BufferGeometry }, woodSlot: MaterialSlot = 'bark'): Prefab => {
  const parts: PrefabPart[] = [];
  if (p.wood) parts.push({ geometry: p.wood, slot: woodSlot, castShadow: true });
  if (p.leaves) parts.push({ geometry: p.leaves, far: thinCards(p.leaves, 3, 1.55), slot: 'foliage', castShadow: true });
  return { parts };
};

const solo = (geometry: BufferGeometry, slot: MaterialSlot, castShadow = true): Prefab => ({ parts: [{ geometry, slot, castShadow }] });

/**
 * Build every prefab a map needs. Deterministic (fixed seeds) so the world looks the same on
 * every device. Run once per map (~150–300 ms: trees carry thousands of leaf cards).
 */
export function buildPrefabs(map: MapDef): PrefabLibrary {
  const lib: PrefabLibrary = new Map();
  const p = map.palette;
  const rng = new Rng(1337, map.id === 'sky' ? 1 : 2);
  const times = <T>(n: number, f: (i: number) => T): T[] => Array.from({ length: n }, (_, i) => f(i));
  const v = (k: HazardKind): number => HAZARDS[k].variants;
  const dv = (k: DecorKind): number => DECOR_VARIANTS[k];

  if (map.id === 'sky') {
    const stone = [rgb(p.rock), mixRGB(rgb(p.rock), rgb(p.rockDark), 0.4), mixRGB(rgb(p.rock), rgb('#a9a6b2'), 0.3)];
    lib.set('boulder', times(v('boulder'), () => solo(smoothRock(rng, { radius: 1.0, height: 1.5, colors: stone, roughness: 0.2 }), 'stone')));
    lib.set('mossBoulder', times(v('mossBoulder'), () => solo(smoothRock(rng, { radius: 1.0, height: 1.45, colors: stone, roughness: 0.24, squash: 0.75 }), 'mossStone')));
    lib.set('tree', times(v('tree'), () => plant(roundTree(rng, p.bark))));
    lib.set('pine', times(v('pine'), () => plant(pineTree(rng, p.bark))));
    lib.set('pillar', times(v('pillar'), () => solo(pillar(rng, p.rock, p.rockDark).build(), 'mossStone')));
    lib.set('gate', times(v('gate'), () => solo(gate(rng, p.rock, p.rockDark), 'mossStone')));
    lib.set('ruin', times(v('ruin'), () => solo(ruin(rng, p.rock, p.rockDark), 'mossStone')));
    lib.set(
      'house',
      times(v('house'), () => {
        const h = house(rng, '#c8613a');
        return { parts: [{ geometry: h.walls, slot: 'plaster', castShadow: true }, { geometry: h.roof, slot: 'roof', castShadow: true }] };
      }),
    );
    lib.set('bush', times(dv('bush'), () => plant(bush(rng))));
    lib.set('waterfall', [solo(waterfallRibbon(130, 3.4), 'waterfall', false)]);
  } else {
    const sand = [rgb(p.rock), mixRGB(rgb(p.rock), rgb(p.rockDark), 0.35), mixRGB(rgb(p.rock), rgb('#f3c08a'), 0.35)];
    lib.set('sandstone', times(v('sandstone'), () => solo(smoothRock(rng, { radius: 1.05, height: 1.6, colors: sand, roughness: 0.16, cleaves: 5 }), 'sandstone')));
    lib.set('arch', times(v('arch'), () => solo(arch(rng, p.rock, p.rockDark), 'sandstone')));
    lib.set('spire', times(v('spire'), () => solo(spire(rng, p.rock, p.rockDark), 'sandstone')));
    lib.set('cactus', times(v('cactus'), () => plant(cactus(rng, '#5a7f3a'), 'cactus')));
    lib.set('deadTree', times(v('deadTree'), () => plant(deadTree(rng, '#8c7a68'))));
    lib.set('mesa', times(v('mesa'), () => solo(mesa(rng, p.rock, p.rockDark), 'sandstone', false)));
    lib.set('dryBush', times(dv('dryBush'), () => plant(dryBush(rng, '#8c6a4a'))));
    lib.set('grassTuft', times(dv('grassTuft'), () => solo(grassTuft(rng, '#6b5a2a', '#e8d29a'), 'tuft', false)));
  }
  return lib;
}
